// Minimal dependency-free external store built on React 18's useSyncExternalStore.
// Chosen over a state library to keep the bundle small and the data-flow explicit and
// testable (the feed orchestrator drives these stores; components subscribe via selectors).

import { useSyncExternalStore } from 'react';

export type Listener = () => void;

export class Store<T extends object> {
  private state: T;
  private readonly listeners = new Set<Listener>();

  constructor(initial: T) {
    this.state = initial;
  }

  get = (): T => this.state;

  set = (patch: Partial<T> | ((s: T) => Partial<T>)): void => {
    const next = typeof patch === 'function' ? patch(this.state) : patch;
    let changed = false;
    for (const k in next) {
      if (!Object.is((this.state as Record<string, unknown>)[k], (next as Record<string, unknown>)[k])) {
        changed = true;
        break;
      }
    }
    if (!changed) return;
    this.state = { ...this.state, ...next };
    for (const l of this.listeners) l();
  };

  subscribe = (l: Listener): (() => void) => {
    this.listeners.add(l);
    return () => {
      this.listeners.delete(l);
    };
  };
}

/**
 * Subscribe to a slice of a store. The selector MUST return a referentially stable value
 * when nothing relevant changed (primitives, or arrays/objects that are only replaced on
 * change) — otherwise React will loop. All selectors in this app follow that rule.
 */
export function useStore<T extends object, S>(store: Store<T>, selector: (s: T) => S): S {
  return useSyncExternalStore(
    store.subscribe,
    () => selector(store.get()),
    () => selector(store.get()),
  );
}
